import { Link } from "react-router-dom";
import Phone from "lucide-react/dist/esm/icons/phone";
import Mail from "lucide-react/dist/esm/icons/mail";
import MapPin from "lucide-react/dist/esm/icons/map-pin";
import User from "lucide-react/dist/esm/icons/user";
import { useContactInfo } from "@/hooks/useCmsData";

const Footer = () => {
  const { data: contact } = useContactInfo();

  const companyName = contact?.company_name || "Ferienwohnungen Luca";
  const street = contact?.street || "Kreuzweg 5";
  const location = contact?.zip && contact?.city ? `D-${contact.zip} ${contact.city}` : "D-27476 Cuxhaven / Döse";
  const phone = contact?.phone || "04721-47760";

  return (
    <footer className="relative text-primary overflow-hidden" style={{ backgroundColor: "hsl(210 12% 92%)" }}>
      <div className="relative z-10 container-narrow mx-auto section-padding">
        <div className="relative grid grid-cols-1 gap-12 md:grid-cols-4 md:gap-0">
          <div className="pointer-events-none absolute inset-y-0 left-1/4 hidden -translate-x-1/2 md:block" aria-hidden="true">
            <span className="block h-full w-px bg-border/90" />
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-2/4 hidden -translate-x-1/2 md:block" aria-hidden="true">
            <span className="block h-full w-px bg-border/90" />
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-3/4 hidden -translate-x-1/2 md:block" aria-hidden="true">
            <span className="block h-full w-px bg-border/90" />
          </div>

          {/* Contact */}
          <div className="relative md:px-8 md:py-3">
            <h3 className="font-display text-xl font-semibold">Kontakt</h3>
            <div className="w-[150px] h-[2px] bg-primary/30 mt-2 mb-4" />
            <address className="not-italic space-y-3 font-body text-sm opacity-90">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  {companyName}<br />
                  {street}<br />
                  {location}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 shrink-0" />
                <span>Ansprechpartner: Olaf Luca</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 shrink-0" />
                <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="hover:underline">{phone}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 shrink-0" />
                <Link to="/kontakt" className="hover:underline">Kontaktformular</Link>
              </div>
            </address>
          </div>

          {/* Auf einen Blick */}
          <nav aria-label="Auf einen Blick" className="relative md:px-8 md:py-3">
            <h3 className="font-display text-xl font-semibold">Auf einen Blick</h3>
            <div className="w-[150px] h-[2px] bg-primary/30 mt-2 mb-4" />
            <ul className="space-y-2 font-body text-sm opacity-90">
              <li><Link to="/#haeuser" className="block hover:underline">Unsere Ferienhäuser</Link></li>
              <li><Link to="/#haus-panorama" className="block hover:underline">Haus Panorama · Duhnen</Link></li>
              <li><Link to="/#haus-strandnixe" className="block hover:underline">Haus Strandnixe · Döse</Link></li>
              <li><Link to="/#bewertungen" className="block hover:underline">Bewertungen</Link></li>
              <li><Link to="/#faq" className="block hover:underline">Häufige Fragen</Link></li>
            </ul>
          </nav>

          {/* Quick links */}
          <nav aria-label="Weitere Seiten" className="relative md:px-8 md:py-3">
            <h3 className="font-display text-xl font-semibold">Schnellzugriff</h3>
            <div className="w-[150px] h-[2px] bg-primary/30 mt-2 mb-4" />
            <ul className="space-y-2 font-body text-sm opacity-90">
              <li><Link to="/" className="block hover:underline">Ferienwohnungen</Link></li>
              <li><Link to="/wissenswertes/die-umgebung" className="block hover:underline">Umgebung</Link></li>
              <li><Link to="/wissenswertes/bildergalerie" className="block hover:underline">Bildergalerie</Link></li>
              <li><Link to="/kontakt" className="block hover:underline">Kontakt</Link></li>
              <li>
                <a
                  href="https://www.nordseeheilbad-cuxhaven.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:underline"
                >
                  Stadt Cuxhaven
                </a>
              </li>
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Rechtliches" className="relative md:px-8 md:py-3">
            <h3 className="font-display text-xl font-semibold">Rechtliches</h3>
            <div className="w-[150px] h-[2px] bg-primary/30 mt-2 mb-4" />
            <ul className="space-y-2 font-body text-sm opacity-90">
              <li><Link to="/impressum" className="block hover:underline">Impressum</Link></li>
              <li><Link to="/datenschutzerklaerung" className="block hover:underline">Datenschutzerklärung</Link></li>
              <li><Link to="/agb" className="block hover:underline">Allgemeine Geschäftsbedingungen</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-settings"))}
                  className="block text-left hover:underline"
                >
                  Cookie-Einstellungen
                </button>
              </li>
            </ul>
          </nav>
        </div>

        <div className="mt-12 pt-8 border-t border-primary/20 text-center">
          <p className="font-body text-sm opacity-70">
            © {new Date().getFullYear()} {companyName} — Vermittlung von Ferienwohnungen an der Nordsee
          </p>
          <a
            href="https://cuxwerk.de"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block font-body text-sm font-bold shimmer-silver hover:opacity-100 transition-opacity duration-300"
          >
            Website by Cuxwerk — Dein Business. Automatisiert.
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;